import { useEffect, useState } from 'react';
import { CheckCircle2, Copy } from 'lucide-react';
import { Button, Card, formatMoney, useTenant } from '@barber/ui';
import type { PixCharge } from '@barber/ui';
import { usePixChargeStatus } from '@/hooks/use-catalog';

/**
 * O PIX para pagar: copia-e-cola, QR e o prazo do desconto correndo.
 *
 * Quem diz que foi pago é o banco (o webhook chega na API); a tela só consulta
 * a cada 3 s. Prazo vencido não desmarca nada — o horário continua, sem o
 * desconto, e isso precisa estar escrito, senão o cliente acha que perdeu a vaga.
 */
export function PixPayment({ charge, onPaid }: { charge: PixCharge; onPaid?: () => void }) {
  const { locale } = useTenant();
  const status = usePixChargeStatus(charge.chargeId);
  const [copiado, setCopiado] = useState(false);
  const restante = useRestante(charge.expiresAt);

  const situacao = status.data?.status ?? charge.status;
  const pago = situacao === 'PAID';
  const vencido = situacao === 'EXPIRED' || (situacao === 'PENDING' && restante <= 0);

  useEffect(() => {
    if (pago) onPaid?.();
  }, [pago, onPaid]);

  if (pago) {
    return (
      <Card className="flex flex-col items-center gap-2 text-center">
        <CheckCircle2 className="h-10 w-10 text-sx-success" aria-hidden="true" />
        <p className="text-lg font-semibold text-content">Pagamento confirmado</p>
        <p className="text-sm text-content-muted">
          {formatMoney(charge.amountCents, locale)} pagos com desconto. É só aparecer no horário.
        </p>
      </Card>
    );
  }

  if (vencido) {
    return (
      <p
        role="status"
        className="rounded-xl border border-sx-warn/40 bg-sx-warn/10 px-4 py-3 text-sm text-content"
      >
        O prazo do desconto acabou — seu horário continua marcado, é só pagar no dia.
      </p>
    );
  }

  const minutos = Math.floor(restante / 60);
  const segundos = String(restante % 60).padStart(2, '0');

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between">
        <span className="text-sm text-content">Pague com PIX</span>
        <span className="text-lg font-bold text-content">
          {formatMoney(charge.amountCents, locale)}
        </span>
      </div>
      {charge.discountCents > 0 && (
        <p className="text-xs text-content-muted">
          <span className="line-through">{formatMoney(charge.baseCents, locale)}</span> · você
          economiza {formatMoney(charge.discountCents, locale)}
        </p>
      )}

      <img src={charge.qrCode} alt="QR Code do PIX" className="mx-auto h-48 w-48" />

      <label className="flex flex-col gap-1 text-xs text-content-muted">
        Copia e cola
        <input
          readOnly
          value={charge.pixCopyPaste}
          className="w-full truncate rounded-lg border border-border bg-surface-muted px-3 py-2 text-xs text-content"
          onFocus={(event) => event.currentTarget.select()}
        />
      </label>

      <Button
        variant="secondary"
        onClick={() => {
          void navigator.clipboard?.writeText(charge.pixCopyPaste).then(() => setCopiado(true));
        }}
      >
        <Copy className="mr-2 h-4 w-4" aria-hidden="true" />
        {copiado ? 'Copiado' : 'Copiar código PIX'}
      </Button>

      <p className="text-center text-xs text-content-muted" aria-live="polite">
        O desconto vale por mais {minutos}:{segundos}. Depois disso, o horário continua marcado e
        você paga no dia.
      </p>
    </Card>
  );
}

/** Segundos até `expiresAt`, atualizados a cada segundo. */
function useRestante(expiresAt: string): number {
  const calcular = () =>
    Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
  const [restante, setRestante] = useState(calcular);

  useEffect(() => {
    const id = setInterval(() => setRestante(calcular()), 1_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiresAt]);

  return restante;
}
